
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

interface NutritionStepProps {
  formData: {
    dietType: string;
    mealsPerDay: string;
    waterIntake: string;
    dietaryRestrictions: string[];
    allergies: string;
  };
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleCheckboxChange: (list: string, value: string) => void;
}

export function NutritionStep({ 
  formData, 
  handleInputChange, 
  handleSelectChange, 
  handleCheckboxChange 
}: NutritionStepProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="dietType">Diet Type <span className="text-destructive">*</span></Label>
        <Select 
          onValueChange={(value) => handleSelectChange("dietType", value)}
          value={formData.dietType}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Select your diet type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="omnivore">Omnivore</SelectItem>
            <SelectItem value="vegetarian">Vegetarian</SelectItem>
            <SelectItem value="vegan">Vegan</SelectItem>
            <SelectItem value="pescatarian">Pescatarian</SelectItem>
            <SelectItem value="keto">Keto</SelectItem>
            <SelectItem value="paleo">Paleo</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="mealsPerDay">Meals Per Day <span className="text-destructive">*</span></Label>
        <Select 
          onValueChange={(value) => handleSelectChange("mealsPerDay", value)}
          value={formData.mealsPerDay}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Select number of meals" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1-2">1-2 meals</SelectItem>
            <SelectItem value="3">3 meals</SelectItem>
            <SelectItem value="4-5">4-5 meals</SelectItem>
            <SelectItem value="6+">6+ meals</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="waterIntake">Water Intake <span className="text-destructive">*</span></Label>
        <Select 
          onValueChange={(value) => handleSelectChange("waterIntake", value)}
          value={formData.waterIntake}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Select water intake" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Less than 4 cups daily</SelectItem>
            <SelectItem value="moderate">4-6 cups daily</SelectItem>
            <SelectItem value="high">6-8 cups daily</SelectItem>
            <SelectItem value="very-high">More than 8 cups daily</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Dietary Restrictions</Label>
        <div className="space-y-2 mt-2">
          {["gluten-free", "dairy-free", "sugar-free", "nut-free", "soy-free", "low-carb", "low-fat"].map((restriction) => (
            <div key={restriction} className="flex items-center space-x-2">
              <Checkbox
                id={restriction}
                checked={(formData.dietaryRestrictions as string[]).includes(restriction)}
                onCheckedChange={() => handleCheckboxChange("dietaryRestrictions", restriction)}
              />
              <Label htmlFor={restriction}>{restriction.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="allergies">Food Allergies</Label>
        <Textarea
          id="allergies"
          name="allergies"
          value={formData.allergies}
          onChange={handleInputChange}
          placeholder="List any food allergies you have"
          className="resize-none"
        />
      </div>
    </div>
  );
}
